import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  Box,
  Collapse,
  LinearProgress,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { toast } from "react-toastify";

/**
 * Generic PDF upload dialog for admin pages.
 *
 * Props:
 *  - open / onClose
 *  - title:           dialog title (e.g. "Upload & Parse PDF Exam")
 *  - description:     explanatory text in the info Alert
 *  - uploadMutation:  RTK Query mutation tuple `[trigger, { isLoading }]`
 *  - fileFieldName:   form-data key for the PDF file (default "pdf")
 *  - extraFields:     array of `{ name, label, type ("text"|"number"|"textarea"|"select"),
 *                                  required, defaultValue, options? (for select) }`
 *  - buildExtraFormData: optional `(values) => FormData entries object` if you need custom shaping
 *  - onParsed:        callback invoked with the parsed result payload
 *  - defaultFormat:   default value for the `format` field sent to the BE
 *                     (one of: "auto" | "toeic-rc" | "toeic-lc" | "thpt"). Default "auto".
 *  - lockFormat:      if true, hides the format selector (always sends `defaultFormat`).
 *  - defaultOcrLang:  default Tesseract language code(s). Default "eng+vie".
 */
const FORMAT_OPTIONS = [
  { value: "auto", label: "Auto-detect" },
  { value: "toeic-rc", label: "TOEIC Reading (Parts 5/6/7)" },
  { value: "toeic-lc", label: "TOEIC Listening" },
  { value: "thpt", label: "Vietnamese THPT" },
];

const PdfUploadDialog = ({
  open,
  onClose,
  title = "Upload & Parse PDF",
  description = "Upload a PDF file. Content will be automatically parsed.",
  uploadMutation,
  fileFieldName = "pdf",
  extraFields = [],
  buildExtraFormData,
  onParsed,
  defaultFormat = "auto",
  lockFormat = false,
  defaultOcrLang = "eng+vie",
}) => {
  const [upload, { isLoading }] = uploadMutation;
  const [file, setFile] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [format, setFormat] = useState(defaultFormat);
  const [ocrLang, setOcrLang] = useState(defaultOcrLang);

  const initialValues = useMemo(
    () =>
      extraFields.reduce((acc, f) => {
        acc[f.name] = f.defaultValue ?? (f.type === "number" ? 0 : "");
        return acc;
      }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(extraFields.map((f) => [f.name, f.defaultValue]))]
  );
  const [values, setValues] = useState(initialValues);

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const reset = () => {
    setFile(null);
    setValues(initialValues);
    setFormat(defaultFormat);
    setOcrLang(defaultOcrLang);
    setShowAdvanced(false);
  };

  const handleClose = () => {
    if (isLoading) return;
    reset();
    onClose?.();
  };

  const requiredMissing = extraFields.some(
    (f) => f.required && (values[f.name] === "" || values[f.name] === null)
  );

  const handleUpload = async () => {
    if (!file) {
      toast.warning("Please select a PDF file");
      return;
    }
    if (requiredMissing) {
      toast.warning("Please fill in all required fields");
      return;
    }

    const formData = new FormData();
    formData.append(fileFieldName, file);

    const extras = buildExtraFormData ? buildExtraFormData(values) : values;
    Object.entries(extras).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") {
        formData.append(k, v);
      }
    });

    // Parser controls (see BE docs/PDF-UPLOAD-API.md)
    if (format) formData.append("format", format);
    if (ocrLang) formData.append("ocrLang", ocrLang);

    try {
      const result = await upload(formData).unwrap();
      toast.success(result?.message || "PDF parsed successfully");
      onParsed?.(result?.data ?? result);
      reset();
      onClose?.();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to upload PDF");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
          {description}
        </Alert>

        {extraFields.map((f) => {
          if (f.type === "hidden") {
            return null;
          }
          if (f.type === "select") {
            return (
              <TextField
                key={f.name}
                select
                label={f.label}
                value={values[f.name] ?? ""}
                onChange={(e) => handleChange(f.name, e.target.value)}
                required={f.required}
                fullWidth
                sx={{ mb: 2 }}
              >
                {(f.options || []).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            );
          }
          return (
            <TextField
              key={f.name}
              label={f.label}
              value={values[f.name] ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                const val =
                  f.type === "number" ? (raw === "" ? "" : Number(raw)) : raw;
                handleChange(f.name, val);
              }}
              type={f.type === "number" ? "number" : "text"}
              multiline={f.type === "textarea"}
              rows={f.type === "textarea" ? 2 : undefined}
              required={f.required}
              fullWidth
              sx={{ mb: 2 }}
            />
          );
        })}

        {!lockFormat && (
          <TextField
            select
            label="PDF format"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Choose the question layout. Use Auto-detect if unsure."
          >
            {FORMAT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<UploadFileIcon />}
            disabled={isLoading}
          >
            {file ? file.name : "Select PDF File"}
            <input
              type="file"
              accept=".pdf,application/pdf"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            onClick={() => setShowAdvanced((s) => !s)}
            endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: "none" }}
          >
            Advanced (OCR options)
          </Button>
          <Collapse in={showAdvanced} unmountOnExit>
            <TextField
              label="OCR language(s)"
              value={ocrLang}
              onChange={(e) => setOcrLang(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
              helperText='Tesseract codes for scanned PDFs. Examples: "eng" (English only — faster), "eng+vie" (default), "vie".'
            />
          </Collapse>
        </Box>

        {isLoading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Parsing PDF — this may take up to a minute for scanned files…
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isLoading || !file || requiredMissing}
        >
          {isLoading ? "Parsing…" : "Upload & Parse"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PdfUploadDialog;
